// src/context/XoeFocusContext.tsx
// Context for tracking the practitioner's current focus within the Xoe application.
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { useXoePresence } from "../hooks/xoe/useXoePresence";
import type { XoeFocus, XoeWheelFocus } from "../types/xoe/XoeFocusTypes";
import { XOE_ROUTE_LABELS, XOE_ROUTE_PREFIX_LABELS, XOE_SECTION_LABELS } from "../types/xoe/XoeFocusTypes";
import type { UserUuid } from "../types/identity";
/**
 * `hasOwnProperty`, not a bare `map[key]` read: an object literal inherits `constructor`,
 * `toString` and friends, and a bare read would hand one of those back as a "label".
 */
const allowListed = (map: Record<string, string>, key: string): string | undefined =>
	Object.prototype.hasOwnProperty.call(map, key) ? map[key] : undefined;

/**
 * Resolve a `pathname` to its allow-listed screen label. An unknown path resolves to
 * `undefined`, NEVER to the raw value — a route added to `App.tsx` is invisible to Xoe
 * until someone adds it here too.
 */
export const xoeRouteLabel = (pathname: string): string | undefined => {
	// A trailing slash is the same screen.
	const path = pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;

	const exact = allowListed(XOE_ROUTE_LABELS, path);
	if (exact) return exact;

	const prefix = Object.keys(XOE_ROUTE_PREFIX_LABELS).find(
		(candidate) => path === candidate || path.startsWith(`${candidate}/`)
	);
	return prefix ? XOE_ROUTE_PREFIX_LABELS[prefix] : undefined;
};

/**
 * Resolve a PatientModal tab key to its allow-listed section label. An unknown key — a new
 * tab, a divider, anything at all — resolves to `undefined`.
 */
export const xoeSectionLabel = (tabKey?: string | null): string | undefined =>
	tabKey ? allowListed(XOE_SECTION_LABELS, tabKey) : undefined;

/** What a publisher hands over. Keys, not labels — see `XoeFocus` above. */
interface XoePatientFocusInput {
	patientId: UserUuid;
	/** *** CLIENT-ONLY *** — see `XoeFocus.patientLabel`. */
	patientLabel?: string;
	/** A PatientModal tab KEY (`"Files"`), resolved here through the allow-list. */
	sectionKey?: string;
}

interface XoeFocusPublisherApi {
	setPatientFocus: (focus: XoePatientFocusInput) => void;
	/**
	 * Clears ONLY if `patientId` is still the focused record. The three sites that render
	 * `<PatientModal>` directly all unmount it on close (`{selectedPatient && …}`), but a
	 * dozen more open one through `useOpenPatientModal`, which owns its OWN instance — so a
	 * page's modal and a widget-driven one are mounted at the same time, and the first
	 * one's cleanup must not be able to wipe a focus the second has just published.
	 */
	clearPatientFocus: (patientId: UserUuid) => void;
	/** Publish the wheel open in the builder (C4). Ids only — see XoeWheelFocus. */
	setWheelFocus: (wheel: XoeWheelFocus) => void;
	/** Clears ONLY if `panelId` is still the focused wheel — clearPatientFocus's rule. */
	clearWheelFocus: (panelId: number) => void;
	/** S13 — a consultation went live (true) or was closed (false). One consult per tab. */
	setConsultFocus: (active: boolean) => void;
	clearConsultFocus: () => void;
}

const XoeFocusContext = createContext<XoeFocus>({});

/**
 * No-ops by default, deliberately. PatientModal publishes from inside itself, and it is
 * mounted by a dozen pages and by every one of their existing test suites — none of which
 * wrap it in this provider. A hook that threw outside the provider would turn "Xoe does
 * not know where you are" into a white screen on the patient record.
 */
const XoeFocusPublisherContext = createContext<XoeFocusPublisherApi>({
	setPatientFocus: () => {},
	clearPatientFocus: () => {},
	setWheelFocus: () => {},
	clearWheelFocus: () => {},
	setConsultFocus: () => {},
	clearConsultFocus: () => {},
});

export const XoeFocusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const location = useLocation();
	const [patientFocus, setPatientFocusState] = useState<XoeFocus | null>(null);
	const [wheelFocus, setWheelFocusState] = useState<XoeWheelFocus | null>(null);
	// Not cleared on navigation: the consult components own it and clear it on unmount.
	const [consultFocus, setConsultFocus] = useState<boolean>(false);

	// Derived during render rather than held in state: the route label is a pure function
	// of the location, and an effect would publish it one commit late — long enough for a
	// turn started on a screen's first paint to be told about the previous screen.
	const routeLabel = xoeRouteLabel(location.pathname);

	// Focus clears on navigation. Guarded on an actual pathname CHANGE, not on the mount
	// run: child effects fire before the parent's, so an unguarded effect here would wipe a
	// focus a PatientModal below had already published in the same commit.
	const lastPathname = useRef(location.pathname);
	useEffect(() => {
		if (lastPathname.current === location.pathname) return;
		lastPathname.current = location.pathname;
		setPatientFocusState(null);
		setWheelFocusState(null);
	}, [location.pathname]);

	// Stable for the provider's lifetime — publishers hold these in effect dependency
	// arrays, so an identity that changed per render would re-publish forever.
	const publisher = useMemo<XoeFocusPublisherApi>(
		() => ({
			setPatientFocus: ({ patientId, patientLabel, sectionKey }) =>
				setPatientFocusState({
					patientId,
					patientLabel,
					sectionLabel: xoeSectionLabel(sectionKey),
				}),
			clearPatientFocus: (patientId) =>
				setPatientFocusState((prev) => (prev?.patientId === patientId ? null : prev)),
			setWheelFocus: (wheel) => setWheelFocusState(wheel),
			clearWheelFocus: (panelId) =>
				setWheelFocusState((prev) => (prev?.panelId === panelId ? null : prev)),
			setConsultFocus: (active) => setConsultFocus(active),
			clearConsultFocus: () => setConsultFocus(false),
		}),
		[]
	);

	const focus = useMemo<XoeFocus>(
		() => ({
			routeLabel,
			...patientFocus,
			...(wheelFocus ? { wheel: wheelFocus } : {}),
			...(consultFocus ? { inConsult: true } : {}),
		}),
		[routeLabel, patientFocus, wheelFocus, consultFocus]
	);

	// S13 — the ONE heartbeat for this tab. Silent, gated on a practitioner token, paused when hidden.
	useXoePresence(focus, consultFocus);

	return (
		<XoeFocusPublisherContext.Provider value={publisher}>
			<XoeFocusContext.Provider value={focus}>{children}</XoeFocusContext.Provider>
		</XoeFocusPublisherContext.Provider>
	);
};

/** Read the current focus. Outside the provider this is `{}` — never a throw. */
export const useXoeFocus = (): XoeFocus => useContext(XoeFocusContext);

/** Publish onto the focus bus. Outside the provider these are no-ops — never a throw. */
export const useXoeFocusPublisher = (): XoeFocusPublisherApi => useContext(XoeFocusPublisherContext);
